<template>
  <WizardContent
    :show-sidebar="false"
    class="wizard-wrapper"
  >
    <template #flashMessage>
      <EduWarningFlashMessage :email="email" />
    </template>
    <GateHeader>
      <template #logo>
        <JobNavIcon />
      </template>
      <template #page-title>
        Welcome back to JobNav!
      </template>
      <template #below-title>
        Don't have an account yet?
        <router-link
          to="signup"
        >
          Sign up now
        </router-link>
      </template>
    </GateHeader>
    <v-form
      ref="form"
      v-model="valid"
      class="form"
      action="/not-found"
      @submit.prevent="submitForm"
    >
      <DpplTextField
        v-model="email"
        class="mb-3"
        label="Email"
        :rules="emailRules"
        validate-on="blur"
      />
      <DpplSignupFormButton
        type="submit"
        block
        :loading="loading"
      >
        Reset Password
      </DpplSignupFormButton>
    </v-form>
  </WizardContent>
</template>

<script>
import { mapActions } from 'pinia';
import WizardContent from '@/components/shared/WizardContent.vue';
import GateHeader from '@/components/shared/gates/GateHeader.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import EduWarningFlashMessage from '@/components/shared/EduWarningFlashMessage.vue';
import { signupSendPasswordResetEmail } from '@/api/signup';
import validation from '@/helpers/validation';
import { useWizard } from '@/stores/wizard/wizard';
import JobNavIcon from '@/assets/jobnav-payscale-icon-logo.svg?component';

export default {
  name: 'PasswordReset',
  components: {
    WizardContent,
    DpplTextField,
    DpplSignupFormButton,
    EduWarningFlashMessage,
    GateHeader,
    JobNavIcon,
  },
  emits: ['track-page-load'],
  data() {
    return {
      email: '',
      emailRules: [
        (v) => !validation.isEmpty(v) || 'Email is required',
        (v) => validation.emailFormat(v) || 'Email format is invalid',
        (v) => validation.emailBlacklist(v) || 'Please provide your work email address.',
        (v) => validation.roleEmailBlacklist(v) || 'Role-based email addresses are not valid. Please use your individual company email address.',
        (v) => (v && v.length <= 250) || 'Email is too long',
      ],
      valid: false,
      loading: false,
    };
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Password Reset' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupSendPasswordResetEmail({
            email: this.email,
          });
          if (responseJSON.success === true) {
            this.$router.push({
              name: 'password-reset-success',
              query: {
                email: this.email,
              },
            });
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Password reset error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        if (responseJSON.errors && responseJSON.errors.sso_mandatory) {
          if (responseJSON.errors.is_google) {
            window.location.href = '/signup/google';
          } else if (responseJSON.errors.is_microsoft) {
            window.location.href = '/signup/microsoft';
          } else {
            window.location.href = `/login/${responseJSON.errors.provider}`;
          }
          return;
        }
        this.updateFlash({ message: 'Something went wrong' });
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.wizard-wrapper {
  min-width: 330px;
  width: 100%;
  align-items: center;
  justify-content: center;
}

.form {
  width: 100%;
}

:deep(.wizard-flash-message) {
  border-top-right-radius: 0px !important;
}

:deep(.slot-flash-message.full-width) {
  width: 450px;
}
</style>
